import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import BarMenuSection from "@/components/bar/BarMenuSection";
import BarDrinkCard from "@/components/bar/BarDrinkCard";
import { barMenuData } from "@/data/barMenuData";
import { barMenuDataEn } from "@/data/barMenuData.en";
import { X, Minimize2, Maximize2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";

interface BarMenuDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const BarMenuDialog = ({ open, onOpenChange }: BarMenuDialogProps) => {
  const { language, t } = useLanguage();
  const [isCompact, setIsCompact] = useState(true);
  
  const currentBarMenuData = language === 'en' ? barMenuDataEn : barMenuData;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl h-[90vh] p-0 bg-background border-gold/20">
        <DialogHeader className="px-3 pt-3 pb-2 border-b border-border/50 bg-luxury/30">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-xl md:text-2xl font-serif font-bold text-gold mb-0.5">
                {t('bar.title')}
              </DialogTitle>
              <p className="text-[11px] text-muted-foreground">
                {t('bar.subtitle')}
              </p>
            </div>
            <div className="flex items-center gap-2 mr-8">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsCompact(!isCompact)}
                className="hover:bg-gold/10 hover:text-gold"
                title={isCompact ? t('bar.enlargeView') : t('bar.minimizeView')}
              >
                {isCompact ? <Maximize2 className="h-5 w-5" /> : <Minimize2 className="h-5 w-5" />}
              </Button>
            </div>
          </div>
        </DialogHeader>
        
        <ScrollArea className="h-full">
          {/* Luxury Navigation Menu */}
          <div className="sticky top-0 z-10 bg-gradient-to-b from-background/98 via-background/95 to-background/90 backdrop-blur-xl border-b border-gold/30 shadow-luxury px-3 py-3">
            <nav className="max-w-5xl mx-auto">
              <div className="flex items-center justify-center gap-4 flex-wrap">
                {/* Vin */}
                <div className="flex-shrink-0 group relative">
                  <button 
                    onClick={() => document.getElementById('vin')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                    className="font-serif text-gold text-sm font-bold tracking-wide hover:text-gold-light transition-all duration-300 whitespace-nowrap relative after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-gradient-to-r after:from-gold after:to-gold-light after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:duration-300 pb-1"
                  >
                    {t('bar.vin')}
                  </button>
                  <div className="absolute top-full left-0 mt-2 bg-[hsl(var(--dark-card))] backdrop-blur-xl border-2 border-gold/40 rounded-lg shadow-luxury p-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50 min-w-[200px]">
                    <div className="flex flex-col gap-2">
                      <button onClick={() => document.getElementById('husets-vin')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} className="text-sm text-foreground hover:text-gold transition-all duration-200 text-left px-3 py-2 rounded hover:bg-gold/10 font-medium">{t('bar.husetsVin')}</button>
                      <button onClick={() => document.getElementById('vitt-vin')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} className="text-sm text-foreground hover:text-gold transition-all duration-200 text-left px-3 py-2 rounded hover:bg-gold/10 font-medium">{t('bar.vittVin')}</button>
                      <button onClick={() => document.getElementById('rott-vin')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} className="text-sm text-foreground hover:text-gold transition-all duration-200 text-left px-3 py-2 rounded hover:bg-gold/10 font-medium">{t('bar.rottVin')}</button>
                      <button onClick={() => document.getElementById('rose-vin')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} className="text-sm text-foreground hover:text-gold transition-all duration-200 text-left px-3 py-2 rounded hover:bg-gold/10 font-medium">{t('bar.roseVin')}</button>
                      <button onClick={() => document.getElementById('prosecco')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} className="text-sm text-foreground hover:text-gold transition-all duration-200 text-left px-3 py-2 rounded hover:bg-gold/10 font-medium">{t('bar.prosecco')}</button>
                    </div>
                  </div>
                </div>

                <div className="w-px h-6 bg-gold/30"></div>

                {/* Cocktails */}
                <button 
                  onClick={() => document.getElementById('cocktails')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} 
                  className="font-serif text-gold text-sm font-bold tracking-wide hover:text-gold-light transition-all duration-300 whitespace-nowrap relative after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-gradient-to-r after:from-gold after:to-gold-light after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:duration-300 pb-1"
                >
                  {t('bar.cocktails')}
                </button>

                <div className="w-px h-6 bg-gold/30"></div>

                {/* Öl */}
                <button 
                  onClick={() => document.getElementById('ol')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} 
                  className="font-serif text-gold text-sm font-bold tracking-wide hover:text-gold-light transition-all duration-300 whitespace-nowrap relative after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-gradient-to-r after:from-gold after:to-gold-light after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:duration-300 pb-1"
                >
                  {t('bar.ol')}
                </button>

                <div className="w-px h-6 bg-gold/30"></div>

                {/* Sprit */}
                <button 
                  onClick={() => document.getElementById('sprit')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} 
                  className="font-serif text-gold text-sm font-bold tracking-wide hover:text-gold-light transition-all duration-300 whitespace-nowrap relative after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-gradient-to-r after:from-gold after:to-gold-light after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:duration-300 pb-1"
                >
                  {t('bar.sprit')}
                </button>
              </div>
            </nav>
          </div>

          <div className="max-w-5xl mx-auto px-3 py-3">
            {/* Vin Section */}
            <div id="vin" className="scroll-mt-20"></div>
            
            {/* Husets Vin */}
            <BarMenuSection 
              id="husets-vin"
              title={t('bar.husetsVin')}
              subtitle={t('bar.husetsVin.subtitle')}
              isCompact={isCompact}
            >
              {currentBarMenuData.husetsVin.map((drink, index) => (
                <BarDrinkCard key={index} {...drink} isCompact={isCompact} />
              ))}
            </BarMenuSection>

            {/* Vitt Vin */}
            <BarMenuSection 
              id="vitt-vin"
              title={t('bar.vittVin')}
              subtitle={t('bar.vittVin.subtitle')}
              isCompact={isCompact}
            >
              {currentBarMenuData.vittVin.map((drink, index) => (
                <BarDrinkCard key={index} {...drink} isCompact={isCompact} />
              ))}
            </BarMenuSection>

            {/* Rött Vin */}
            <BarMenuSection 
              id="rott-vin"
              title={t('bar.rottVin')}
              subtitle={t('bar.rottVin.subtitle')}
              isCompact={isCompact}
            >
              {currentBarMenuData.rottVin.map((drink, index) => (
                <BarDrinkCard key={index} {...drink} isCompact={isCompact} />
              ))}
            </BarMenuSection>

            {/* Rosévin */}
            <BarMenuSection 
              id="rose-vin"
              title={t('bar.roseVin')}
              subtitle={t('bar.roseVin.subtitle')}
              isCompact={isCompact}
            >
              {currentBarMenuData.roseVin.map((drink, index) => (
                <BarDrinkCard key={index} {...drink} isCompact={isCompact} />
              ))}
            </BarMenuSection>

            {/* Prosecco */}
            <BarMenuSection 
              id="prosecco"
              title={t('bar.prosecco')}
              subtitle={t('bar.prosecco.subtitle')}
              isCompact={isCompact}
            >
              {currentBarMenuData.prosecco.map((drink, index) => (
                <BarDrinkCard key={index} {...drink} isCompact={isCompact} />
              ))}
            </BarMenuSection>

            {/* Cocktails */}
            <BarMenuSection 
              id="cocktails"
              title={t('bar.cocktails')}
              subtitle={t('bar.cocktails.subtitle')}
              isCompact={isCompact}
            >
              {currentBarMenuData.cocktails.map((drink, index) => (
                <BarDrinkCard key={index} {...drink} isCompact={isCompact} />
              ))}
            </BarMenuSection>

            {/* Öl */}
            <BarMenuSection 
              id="ol"
              title={t('bar.ol')}
              subtitle={t('bar.ol.subtitle')}
              isCompact={isCompact}
            >
              {currentBarMenuData.ol.map((drink, index) => (
                <BarDrinkCard key={index} {...drink} isCompact={isCompact} />
              ))}
            </BarMenuSection>

            {/* Sprit */}
            <BarMenuSection 
              id="sprit"
              title={t('bar.sprit')}
              subtitle={t('bar.sprit.subtitle')}
              isCompact={isCompact}
            >
              {currentBarMenuData.sprit.map((drink, index) => (
                <BarDrinkCard key={index} {...drink} isCompact={isCompact} />
              ))}
            </BarMenuSection>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default BarMenuDialog;
